import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from './App';
import { 
    FaUser, FaCog, FaUsers, FaBoxOpen, FaChartLine, FaHistory, 
    FaCoins, FaGift, FaGamepad, FaTimes, FaQuestionCircle, FaInfoCircle, FaSync, FaCopy, FaSpinner
} from 'react-icons/fa';

const BACKEND_URL = window.location.hostname === 'localhost' ? 'http://localhost:4000' : 'https://test3-0ar4.onrender.com';

export default function ProfilePage({ initialTab = 'profile' }) {
    const { user, setUser } = useContext(UserContext);
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(initialTab);
    const [chartTab, setChartTab] = useState('Weekly');
    const [betHistory, setBetHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [gameFilter, setGameFilter] = useState('all');
    const [showGameDropdown, setShowGameDropdown] = useState(false);

    // Affiliate state
    const [referralData, setReferralData] = useState(null);
    const [affiliateCode, setAffiliateCode] = useState('');
    const [affiliateLoading, setAffiliateLoading] = useState(false);
    const [affiliateStatus, setAffiliateStatus] = useState(null);

    // Settings state
    const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [passwordStatus, setPasswordStatus] = useState(null);

    const handlePasswordChange = async () => {
        if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
            setPasswordStatus({ type: 'error', message: 'All fields are required' });
            return;
        }
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setPasswordStatus({ type: 'error', message: 'New passwords do not match' });
            return;
        }
        if (passwordData.newPassword.length < 8) {
            setPasswordStatus({ type: 'error', message: 'Password must be at least 8 characters' });
            return;
        }

        try {
            setPasswordLoading(true);
            setPasswordStatus(null);
            const token = localStorage.getItem('token');
            const response = await fetch(`${BACKEND_URL}/api/auth/change-password`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    currentPassword: passwordData.currentPassword,
                    newPassword: passwordData.newPassword
                })
            });
            const data = await response.json();
            if (response.ok) {
                setPasswordStatus({ type: 'success', message: 'Password updated successfully!' });
                setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
            } else {
                setPasswordStatus({ type: 'error', message: data.error || 'Failed to update password' });
            }
        } catch (error) {
            setPasswordStatus({ type: 'error', message: 'Server error. Please try again.' });
        } finally {
            setPasswordLoading(false);
        }
    };

    const fetchReferralData = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${BACKEND_URL}/api/referral/stats`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setReferralData(data);
            }
        } catch (error) {
            console.error('Error fetching referral data:', error);
        }
    };

    const createOrChangeCode = async () => {
        if (!affiliateCode.trim()) return;
        try {
            setAffiliateLoading(true);
            setAffiliateStatus(null);
            const token = localStorage.getItem('token');
            // Try change-code first (works if user already has a code), otherwise apply
            const endpoint = user?.referralCode ? 'change-code' : 'apply';
            const response = await fetch(`${BACKEND_URL}/api/referral/${endpoint}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ code: affiliateCode.trim() })
            });
            const data = await response.json();
            if (response.ok) {
                setAffiliateStatus({ type: 'success', message: data.message || 'Affiliate code set!' });
                fetchReferralData();
                // Refresh user data
                const updatedUser = await fetch(`${BACKEND_URL}/api/auth/me`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (updatedUser.ok) {
                    const userData = await updatedUser.json();
                    setUser(userData);
                }
            } else {
                setAffiliateStatus({ type: 'error', message: data.error || 'Failed to set code' });
            }
        } catch (error) {
            setAffiliateStatus({ type: 'error', message: 'Failed to set affiliate code' });
        } finally {
            setAffiliateLoading(false);
        }
    };

    const claimReferralRewards = async () => {
        try {
            setAffiliateLoading(true);
            const token = localStorage.getItem('token');
            const response = await fetch(`${BACKEND_URL}/api/referral/claim`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setAffiliateStatus({ type: 'success', message: `Claimed ${data.totalRewards?.toFixed(2) || '0.00'} in rewards!` });
                fetchReferralData();
                const updatedUser = await fetch(`${BACKEND_URL}/api/auth/me`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (updatedUser.ok) {
                    const userData = await updatedUser.json();
                    setUser(userData);
                }
            } else {
                const error = await response.json();
                setAffiliateStatus({ type: 'error', message: error.error || 'Nothing to claim' });
            }
        } catch (error) {
            setAffiliateStatus({ type: 'error', message: 'Failed to claim rewards' });
        } finally {
            setAffiliateLoading(false);
        }
    };

    const fetchBetHistory = async () => {
        try {
            setLoadingHistory(true);
            const token = localStorage.getItem('token');
            const url = new URL(`${BACKEND_URL}/api/wager/history`);
            url.searchParams.append('limit', '50');
            if (gameFilter !== 'all') {
                url.searchParams.append('gameType', gameFilter);
            }
            const res = await fetch(url.toString(), {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.wagers) {
                setBetHistory(data.wagers);
            }
        } catch (e) {
            console.error('Failed to fetch bet history', e);
        } finally {
            setLoadingHistory(false);
        }
    };

    useEffect(() => {
        setActiveTab(initialTab);
    }, [initialTab]);

    useEffect(() => {
        if (activeTab === 'betting-history') {
            fetchBetHistory();
        }
        if (activeTab === 'affiliates') {
            fetchReferralData();
        }
    }, [activeTab, gameFilter]);

    return (
        <div style={{ display: 'flex', padding: '24px', gap: '24px', maxWidth: '1200px', margin: '0 auto', width: '100%', color: '#fff' }}>
            
            {/* Left Sub-Navigation */}
            <div style={{ width: '200px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {[
                    { id: 'profile', label: 'Profile', icon: <FaUser size={14} /> },
                    { id: 'settings', label: 'Settings', icon: <FaCog size={14} /> },
                    { id: 'affiliates', label: 'Affiliates', icon: <FaUsers size={14} /> },
                    { id: 'cases', label: 'Cases', icon: <FaBoxOpen size={14} /> },
                    { id: 'betting-history', label: 'Betting History', icon: <FaGamepad size={14} /> },
                    { id: 'transactions', label: 'Transactions', icon: <FaHistory size={14} /> },
                ].map(item => (
                    <button
                        key={item.id}
                        onClick={() => {
                            setActiveTab(item.id);
                            navigate(`/dashboard?tab=${item.id}`);
                        }}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px',
                            background: activeTab === item.id ? 'linear-gradient(135deg, #ffb703, #d97706)' : 'transparent',
                            color: activeTab === item.id ? '#000' : '#9ca3af',
                            border: 'none', borderRadius: '8px', cursor: 'pointer',
                            fontSize: '14px', fontWeight: 800, transition: 'all 0.2s', textAlign: 'left'
                        }}
                    >
                        {item.icon} {item.label}
                    </button>
                ))}
            </div>

            {/* Right Content Area */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '24px' }}>
                
                {/* Top User Card */}
                <div style={{ background: '#16191f', borderRadius: '12px', padding: '24px', border: '1px solid #1a1c22' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#1a1c22', overflow: 'hidden', border: '2px solid #2d3748' }}>
                                <img src={user?.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user?.username}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            </div>
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                    <span style={{ background: '#2d3748', color: '#9ca3af', padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: 800, fontStyle: 'italic' }}>0</span>
                                    <span style={{ fontSize: '18px', fontWeight: 900 }}>{user?.username || 'user'}</span>
                                </div>
                                <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: 600 }}>Joined January 16, 2026</div>
                            </div>
                        </div>
                        <div style={{ background: '#1a1c22', padding: '6px 12px', borderRadius: '6px', fontSize: '11px', color: '#9ca3af', fontWeight: 700, border: '1px solid #2d3748' }}>
                            RC-ID: <span style={{ color: '#fff', fontWeight: 800 }}>{user?.id || user?._id || 'ph27utvuqu'}</span>
                        </div>
                    </div>

                    {/* Progress Bar */}
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '12px', color: '#9ca3af', fontWeight: 700 }}>Current level:</span>
                                <span style={{ background: '#1a1c22', color: '#9ca3af', padding: '2px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 800, fontStyle: 'italic', border: '1px solid #2d3748' }}>{user?.level?.current || 1}</span>
                            </div>
                            <div style={{ fontSize: '12px', color: '#fff', fontWeight: 800 }}>
                                {Math.floor(user?.level?.progress || 0)} <span style={{ color: '#9ca3af' }}>/ 100 XP</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '12px', color: '#9ca3af', fontWeight: 700 }}>Next level:</span>
                                <span style={{ background: '#1a1c22', color: '#9ca3af', padding: '2px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 800, fontStyle: 'italic', border: '1px solid #2d3748' }}>{(user?.level?.current || 1) + 1}</span>
                            </div>
                        </div>
                        <div style={{ width: '100%', height: '8px', background: '#111318', borderRadius: '4px', overflow: 'hidden', border: '1px solid #1a1c22' }}>
                            <div style={{ width: `${user?.level?.progress || 0}%`, height: '100%', background: '#ffb703' }} />
                        </div>
                    </div>
                </div>

                {/* Main Content Area Based on Active Tab */}
                {activeTab === 'profile' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        {/* Stats Row */}
                        <div style={{ display: 'flex', gap: '16px' }}>
                            <div style={{ flex: 1, background: '#16191f', borderRadius: '12px', padding: '16px', border: '1px solid #1a1c22', display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: '#1a1c22', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
                                    <FaCoins size={16} />
                                </div>
                                <div>
                                    <div style={{ fontSize: '12px', color: '#9ca3af', fontWeight: 700, marginBottom: '2px' }}>Wagered</div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '16px', fontWeight: 900, color: '#fff' }}>
                                        <img src="/scrap.webp" alt="coins" style={{ width: '12px' }} /> {(user?.totalWagered || 0).toFixed(2)}
                                    </div>
                                </div>
                            </div>
                            <div style={{ flex: 1, background: '#16191f', borderRadius: '12px', padding: '16px', border: '1px solid #1a1c22', display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: '#1a1c22', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
                                    <FaCoins size={16} />
                                </div>
                                <div>
                                    <div style={{ fontSize: '12px', color: '#9ca3af', fontWeight: 700, marginBottom: '2px' }}>Bets placed</div>
                                    <div style={{ fontSize: '16px', fontWeight: 900, color: '#fff' }}>{user?.stats?.betsPlaced || 0}</div>
                                </div>
                            </div>
                            <div style={{ flex: 1, background: '#064e3b', borderRadius: '12px', padding: '16px', border: '1px solid #047857', display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: '#047857', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                                    <FaGift size={16} />
                                </div>
                                <div>
                                    <div style={{ fontSize: '12px', color: '#6ee7b7', fontWeight: 700, marginBottom: '2px' }}>Rewards claimed</div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '16px', fontWeight: 900, color: '#fff' }}>
                                        <img src="/scrap.webp" alt="coins" style={{ width: '12px' }} /> {(user?.stats?.rewardsClaimed || 0).toFixed(2)}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div style={{ fontSize: '18px', fontWeight: 900, marginTop: '8px' }}>Statistics</div>

                        {/* XP Cards */}
                        <div style={{ display: 'flex', gap: '16px' }}>
                            <div style={{ flex: 1, background: 'linear-gradient(90deg, #422006 0%, #16191f 100%)', borderRadius: '12px', padding: '20px', border: '1px solid #713f12', position: 'relative', overflow: 'hidden' }}>
                                <div style={{ position: 'relative', zIndex: 2 }}>
                                    <div style={{ fontSize: '11px', color: '#d97706', fontWeight: 800, textTransform: 'uppercase', marginBottom: '4px' }}>XP LAST 7 DAYS</div>
                                    <div style={{ fontSize: '24px', fontWeight: 900, color: '#fff' }}>0</div>
                                </div>
                                <div style={{ position: 'absolute', left: '-20px', top: '50%', transform: 'translateY(-50%)', fontSize: '100px', filter: 'drop-shadow(0 10px 10px rgba(0,0,0,0.5))', opacity: 0.9 }}>⭐</div>
                            </div>
                            <div style={{ flex: 1, background: 'linear-gradient(90deg, #064e3b 0%, #16191f 100%)', borderRadius: '12px', padding: '20px', border: '1px solid #047857', position: 'relative', overflow: 'hidden' }}>
                                <div style={{ position: 'relative', zIndex: 2 }}>
                                    <div style={{ fontSize: '11px', color: '#10b981', fontWeight: 800, textTransform: 'uppercase', marginBottom: '4px' }}>XP LAST 30 DAYS</div>
                                    <div style={{ fontSize: '24px', fontWeight: 900, color: '#fff' }}>0</div>
                                </div>
                                <div style={{ position: 'absolute', left: '-20px', top: '50%', transform: 'translateY(-50%)', fontSize: '100px', filter: 'drop-shadow(0 10px 10px rgba(0,0,0,0.5))', opacity: 0.9, filter: 'hue-rotate(120deg)' }}>⭐</div>
                            </div>
                        </div>

                        {/* Info Row */}
                        <div style={{ display: 'flex', gap: '16px' }}>
                            {[
                                { title: 'Most played', value: 'None', icon: <FaGamepad /> },
                                { title: 'Biggest win', value: <><img src="/scrap.webp" alt="coins" style={{ width: '12px', marginRight: '4px' }} />0.00</>, icon: <FaCoins /> },
                                { title: 'Highest multiplier', value: 'x0', icon: <FaTimes /> },
                                { title: 'Favorite case', value: 'None', icon: <FaQuestionCircle /> },
                            ].map((item, i) => (
                                <div key={i} style={{ flex: 1, background: '#16191f', borderRadius: '12px', padding: '16px', border: '1px solid #1a1c22', display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div style={{ width: '32px', height: '32px', borderRadius: '6px', background: '#1a1c22', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
                                        {item.icon}
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 700, marginBottom: '4px' }}>{item.title}</div>
                                        <div style={{ fontSize: '14px', fontWeight: 900, color: '#fff', display: 'flex', alignItems: 'center' }}>{item.value}</div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#9ca3af', fontSize: '12px', fontWeight: 700 }}>
                            <FaInfoCircle /> How is XP calculated?
                        </div>

                        {/* Chart Area */}
                        <div style={{ background: '#16191f', borderRadius: '12px', padding: '24px', border: '1px solid #1a1c22', minHeight: '300px' }}>
                            <div style={{ display: 'flex', gap: '4px', marginBottom: '24px' }}>
                                {['Weekly', 'Monthly', 'Yearly', 'All Time'].map(tab => (
                                    <button
                                        key={tab}
                                        onClick={() => setChartTab(tab)}
                                        style={{
                                            padding: '8px 16px',
                                            background: chartTab === tab ? 'rgba(255, 183, 3, 0.15)' : '#1a1c22',
                                            color: chartTab === tab ? '#ffb703' : '#6b7280',
                                            border: chartTab === tab ? '1px solid rgba(255, 183, 3, 0.3)' : '1px solid transparent',
                                            borderRadius: '8px', fontSize: '12px', fontWeight: 800, cursor: 'pointer', transition: 'all 0.2s'
                                        }}
                                    >
                                        {tab}
                                    </button>
                                ))}
                            </div>
                            
                            {/* Dummy Chart */}
                            <div style={{ position: 'relative', height: '200px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', color: '#6b7280', fontSize: '11px', fontWeight: 700 }}>
                                {[5.0, 4.0, 3.0, 2.0].map(val => (
                                    <div key={val} style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                        <span style={{ width: '20px', textAlign: 'right' }}>{val.toFixed(1)}</span>
                                        <div style={{ flex: 1, borderTop: '1px dashed #1a1c22' }} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'betting-history' && (
                    <div style={{ background: '#16191f', borderRadius: '12px', padding: '24px', border: '1px solid #1a1c22', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ fontSize: '20px', fontWeight: 900, color: '#fff' }}>Betting history</div>
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                <div style={{ position: 'relative' }}>
                                    <div 
                                        onClick={() => setShowGameDropdown(!showGameDropdown)}
                                        style={{ background: '#1a1c22', borderRadius: '8px', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px', border: '1px solid #2d3748', cursor: 'pointer', minWidth: '120px', justifyContent: 'space-between' }}
                                    >
                                        <span style={{ color: '#9ca3af', fontSize: '13px', fontWeight: 700 }}>Game: <span style={{ color: '#fff', fontWeight: 800 }}>
                                            {{
                                                'all': 'All',
                                                'coinflip': 'Coinflip',
                                                'jackpot': 'Jackpot',
                                                'cases': 'Case Battles',
                                                'case_opening': 'Cases',
                                                'bust': 'Bust'
                                            }[gameFilter] || 'All'}
                                        </span></span>
                                        <span style={{ color: '#6b7280', fontSize: '10px', transition: 'transform 0.2s', transform: showGameDropdown ? 'rotate(180deg)' : 'rotate(0)' }}>▼</span>
                                    </div>

                                    {showGameDropdown && (
                                        <>
                                            <div style={{ position: 'fixed', inset: 0, zIndex: 10 }} onClick={() => setShowGameDropdown(false)} />
                                            <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: '160px', background: '#1a1c22', border: '1px solid #2d3748', borderRadius: '8px', padding: '4px', zIndex: 11, boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.5)' }}>
                                                {[
                                                    { id: 'all', label: 'All' },
                                                    { id: 'coinflip', label: 'Coinflip' },
                                                    { id: 'jackpot', label: 'Jackpot' },
                                                    { id: 'cases', label: 'Case Battles' },
                                                    { id: 'case_opening', label: 'Cases' },
                                                    { id: 'bust', label: 'Bust' }
                                                ].map(game => (
                                                    <div 
                                                        key={game.id}
                                                        onClick={() => { setGameFilter(game.id); setShowGameDropdown(false); }}
                                                        style={{ 
                                                            padding: '8px 12px', 
                                                            borderRadius: '6px', 
                                                            fontSize: '13px', 
                                                            fontWeight: 700, 
                                                            color: gameFilter === game.id ? '#fff' : '#9ca3af',
                                                            background: gameFilter === game.id ? 'rgba(255, 183, 3, 0.1)' : 'transparent',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s'
                                                        }}
                                                        onMouseEnter={(e) => { if (gameFilter !== game.id) e.currentTarget.style.background = '#252731'; }}
                                                        onMouseLeave={(e) => { if (gameFilter !== game.id) e.currentTarget.style.background = 'transparent'; }}
                                                    >
                                                        {game.label}
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>
                                <button onClick={fetchBetHistory} style={{ width: '36px', height: '36px', borderRadius: '8px', background: '#1a1c22', border: '1px solid #2d3748', color: '#9ca3af', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                                    <FaSync size={14} className={loadingHistory ? 'fa-spin' : ''} />
                                </button>
                            </div>
                        </div>

                        {/* Table Header */}
                        <div style={{ display: 'flex', background: '#111318', padding: '12px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 800, color: '#9ca3af' }}>
                            <div style={{ flex: 1 }}>Game</div>
                            <div style={{ flex: 1 }}>User</div>
                            <div style={{ flex: 1 }}>Bet</div>
                            <div style={{ flex: 1 }}>Multi</div>
                            <div style={{ flex: 1 }}>Payout</div>
                        </div>

                        {/* Table Body */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {loadingHistory ? (
                                <div style={{ padding: '60px 0', textAlign: 'center', color: '#6b7280', fontSize: '14px', fontWeight: 700 }}>
                                    Loading history...
                                </div>
                            ) : betHistory.length === 0 ? (
                                <div style={{ padding: '60px 0', textAlign: 'center', color: '#6b7280', fontSize: '14px', fontWeight: 700 }}>
                                    No betting history found.
                                </div>
                            ) : (
                                betHistory.map((bet, idx) => (
                                    <div key={bet._id || idx} style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', background: idx % 2 === 0 ? 'transparent' : 'rgba(26, 28, 34, 0.5)', borderRadius: '8px', fontSize: '13px', fontWeight: 800, color: '#fff', borderBottom: idx !== betHistory.length - 1 ? '1px solid rgba(26, 28, 34, 0.5)' : 'none' }}>
                                        <div style={{ flex: 1, textTransform: 'capitalize', color: '#9ca3af' }}>
                                            {bet.gameType ? bet.gameType.replace('_', ' ') : 'Unknown'}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            {user?.username || 'You'}
                                        </div>
                                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <img src="/scrap.webp" alt="" style={{ width: '12px' }} />
                                            {bet.amount?.toFixed(2)}
                                        </div>
                                        <div style={{ flex: 1, color: (bet.profit > 0 || bet.multiplier > 1) ? '#10b981' : (bet.profit === 0 && bet.amount > 0 ? '#ffb703' : '#9ca3af') }}>
                                            {(() => {
                                                const m = bet.multiplier || (bet.amount > 0 ? (bet.profit + bet.amount) / bet.amount : 0);
                                                return m > 0 ? `${m.toFixed(2)}x` : '0.00x';
                                            })()}
                                        </div>
                                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '4px', color: bet.outcome === 'win' ? '#10b981' : (bet.outcome === 'loss' ? '#ef4444' : '#9ca3af') }}>
                                            <img src="/scrap.webp" alt="" style={{ width: '12px' }} />
                                            {bet.outcome === 'win' ? `+${(bet.profit + bet.amount).toFixed(2)}` : (bet.outcome === 'loss' ? `-${bet.amount?.toFixed(2)}` : '0.00')}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'affiliates' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        {/* Header Banner */}
                        <div style={{ background: '#16191f', borderRadius: '12px', border: '1px solid #1a1c22', overflow: 'hidden', position: 'relative', display: 'flex', minHeight: '160px' }}>
                            <div style={{ flex: 1, padding: '24px', display: 'flex', flexDirection: 'column', justifyContent: 'center', zIndex: 2 }}>
                                <div style={{ fontSize: '20px', fontWeight: 900, color: '#ffb703', marginBottom: '4px' }}>
                                    {referralData?.totalReferrals || user?.referralCount || 0} <span style={{ color: '#fff' }}>Affiliates</span>
                                </div>
                                <div style={{ fontSize: '13px', color: '#9ca3af', fontWeight: 700, marginBottom: '8px' }}>
                                    {referralData?.totalReferrals || user?.referralCount || 0} active affiliates
                                </div>

                                {user?.referralCode && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                                        <span style={{ fontSize: '13px', color: '#9ca3af', fontWeight: 700 }}>Your code:</span>
                                        <span style={{ background: '#1a1c22', border: '1px solid #ffb703', borderRadius: '6px', padding: '4px 12px', color: '#ffb703', fontSize: '14px', fontWeight: 900 }}>{user.referralCode}</span>
                                        <button 
                                            onClick={() => { navigator.clipboard.writeText(user.referralCode); setAffiliateStatus({ type: 'success', message: 'Code copied!' }); setTimeout(() => setAffiliateStatus(null), 2000); }}
                                            style={{ background: 'transparent', border: 'none', color: '#ffb703', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                                        >
                                            <FaCopy size={14} />
                                        </button>
                                    </div>
                                )}
                                
                                <div style={{ display: 'flex', gap: '8px', maxWidth: '400px' }}>
                                    <input 
                                        type="text" 
                                        placeholder={user?.referralCode ? "Change affiliate code" : "Enter affiliate code"}
                                        value={affiliateCode}
                                        onChange={(e) => setAffiliateCode(e.target.value)}
                                        style={{ flex: 1, background: '#1a1c22', border: '1px solid #2d3748', borderRadius: '8px', padding: '12px 16px', color: '#fff', fontSize: '14px', fontWeight: 600, outline: 'none' }} 
                                    />
                                    <button 
                                        onClick={createOrChangeCode}
                                        disabled={affiliateLoading || !affiliateCode.trim()}
                                        style={{ background: '#ffb703', color: '#000', border: 'none', borderRadius: '8px', padding: '0 24px', fontSize: '14px', fontWeight: 900, cursor: affiliateLoading ? 'wait' : 'pointer', transition: 'background 0.2s', opacity: !affiliateCode.trim() ? 0.5 : 1 }}
                                    >
                                        {affiliateLoading ? <FaSpinner className="fa-spin" /> : (user?.referralCode ? 'Change' : 'Create')}
                                    </button>
                                </div>

                                {affiliateStatus && (
                                    <div style={{ marginTop: '12px', fontSize: '13px', fontWeight: 700, color: affiliateStatus.type === 'success' ? '#10b981' : '#ef4444' }}>
                                        {affiliateStatus.message}
                                    </div>
                                )}
                            </div>
                            
                            {/* Graphic on right */}
                            <div style={{ width: '40%', position: 'relative', overflow: 'hidden' }}>
                                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, #16191f 0%, transparent 100%)', zIndex: 1 }} />
                                <div style={{ width: '100%', height: '100%', background: 'radial-gradient(circle at center, rgba(255, 183, 3, 0.2) 0%, transparent 70%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffb703', fontSize: '48px', opacity: 0.5 }}>
                                    <FaGift />
                                </div>
                            </div>
                        </div>

                        {/* Overview Section */}
                        <div>
                            <div style={{ fontSize: '16px', fontWeight: 900, color: '#fff', marginBottom: '16px' }}>Overview</div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                                {[
                                    { label: 'Deposited', value: (referralData?.totalDeposited || 0).toFixed(2), icon: <FaCoins />, hasCoins: true },
                                    { label: 'Wagered', value: (referralData?.totalWagered || 0).toFixed(2), icon: <FaCoins />, hasCoins: true },
                                    { label: 'Total commission', value: (referralData?.referralEarnings || user?.referralEarnings || 0).toFixed(2), icon: <FaCoins />, hasCoins: true },
                                    { label: 'Affiliates', value: String(referralData?.totalReferrals || user?.referralCount || 0), icon: <FaUsers /> },
                                    { label: 'FTDs', value: String(referralData?.ftds || 0), icon: <FaUsers /> },
                                    { label: 'Claimable', value: (referralData?.pendingRewards || 0).toFixed(2), icon: <FaCoins />, highlight: true, hasCoins: true }
                                ].map((stat, i) => (
                                    <div key={i} style={{ background: stat.highlight ? 'rgba(16, 185, 129, 0.1)' : '#16191f', borderRadius: '12px', padding: '16px', border: stat.highlight ? '1px solid rgba(16, 185, 129, 0.2)' : '1px solid #1a1c22', display: 'flex', gap: '16px', alignItems: 'center', cursor: stat.highlight ? 'pointer' : 'default' }} onClick={stat.highlight ? claimReferralRewards : undefined}>
                                        <div style={{ color: stat.highlight ? '#10b981' : '#6b7280', fontSize: '20px' }}>
                                            {stat.icon}
                                        </div>
                                        <div>
                                            <div style={{ color: stat.highlight ? '#10b981' : '#9ca3af', fontSize: '13px', fontWeight: 700, marginBottom: '4px' }}>{stat.label}{stat.highlight ? ' (click to claim)' : ''}</div>
                                            <div style={{ color: '#fff', fontSize: '16px', fontWeight: 900, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                {stat.hasCoins && <img src="/scrap.webp" alt="coins" style={{ width: '14px' }} />}
                                                {stat.value}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Referred Users List */}
                        {referralData?.referredUsers && referralData.referredUsers.length > 0 && (
                            <div>
                                <div style={{ fontSize: '16px', fontWeight: 900, color: '#fff', marginBottom: '16px' }}>Referred Users</div>
                                <div style={{ background: '#16191f', borderRadius: '12px', border: '1px solid #1a1c22', overflow: 'hidden' }}>
                                    <div style={{ display: 'flex', background: '#111318', padding: '12px 16px', fontSize: '13px', fontWeight: 800, color: '#9ca3af' }}>
                                        <div style={{ flex: 1 }}>User</div>
                                        <div style={{ flex: 1 }}>Wagered</div>
                                        <div style={{ flex: 1 }}>Commission</div>
                                        <div style={{ flex: 1 }}>Joined</div>
                                    </div>
                                    {referralData.referredUsers.map((ref, idx) => (
                                        <div key={idx} style={{ display: 'flex', padding: '12px 16px', fontSize: '13px', fontWeight: 800, color: '#fff', borderBottom: '1px solid #1a1c22' }}>
                                            <div style={{ flex: 1 }}>{ref.username}</div>
                                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <img src="/scrap.webp" alt="" style={{ width: '12px' }} />
                                                {(ref.totalWagered || 0).toFixed(2)}
                                            </div>
                                            <div style={{ flex: 1, color: '#10b981' }}>${ref.commission || '0.00'}</div>
                                            <div style={{ flex: 1, color: '#9ca3af' }}>{ref.joinedAt ? new Date(ref.joinedAt).toLocaleDateString() : '-'}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'settings' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        <div style={{ background: '#16191f', borderRadius: '12px', padding: '24px', border: '1px solid #1a1c22' }}>
                            <div style={{ fontSize: '18px', fontWeight: 900, marginBottom: '20px' }}>Security Settings</div>
                            
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '400px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '12px', color: '#9ca3af', fontWeight: 700, marginBottom: '8px' }}>Current Password</label>
                                    <input 
                                        type="password"
                                        value={passwordData.currentPassword}
                                        onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                                        style={{ width: '100%', background: '#111318', border: '1px solid #1a1c22', borderRadius: '8px', padding: '12px', color: '#fff', fontSize: '14px', outline: 'none' }}
                                        placeholder="••••••••"
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '12px', color: '#9ca3af', fontWeight: 700, marginBottom: '8px' }}>New Password</label>
                                    <input 
                                        type="password"
                                        value={passwordData.newPassword}
                                        onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                                        style={{ width: '100%', background: '#111318', border: '1px solid #1a1c22', borderRadius: '8px', padding: '12px', color: '#fff', fontSize: '14px', outline: 'none' }}
                                        placeholder="••••••••"
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '12px', color: '#9ca3af', fontWeight: 700, marginBottom: '8px' }}>Confirm New Password</label>
                                    <input 
                                        type="password"
                                        value={passwordData.confirmPassword}
                                        onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                                        style={{ width: '100%', background: '#111318', border: '1px solid #1a1c22', borderRadius: '8px', padding: '12px', color: '#fff', fontSize: '14px', outline: 'none' }}
                                        placeholder="••••••••"
                                    />
                                </div>

                                {passwordStatus && (
                                    <div style={{ padding: '12px', borderRadius: '8px', background: passwordStatus.type === 'success' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)', color: passwordStatus.type === 'success' ? '#10b981' : '#ef4444', fontSize: '13px', fontWeight: 700 }}>
                                        {passwordStatus.message}
                                    </div>
                                )}

                                <button 
                                    onClick={handlePasswordChange}
                                    disabled={passwordLoading}
                                    style={{ 
                                        background: '#ffb703', 
                                        color: '#000', 
                                        border: 'none', 
                                        borderRadius: '8px', 
                                        padding: '12px', 
                                        fontSize: '14px', 
                                        fontWeight: 900, 
                                        cursor: passwordLoading ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.2s',
                                        opacity: passwordLoading ? 0.7 : 1
                                    }}
                                >
                                    {passwordLoading ? <FaSpinner className="fa-spin" /> : 'Update Password'}
                                </button>
                            </div>
                        </div>

                        <div style={{ background: '#16191f', borderRadius: '12px', padding: '24px', border: '1px solid #1a1c22' }}>
                            <div style={{ fontSize: '18px', fontWeight: 900, marginBottom: '20px' }}>Account Information</div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px' }}>
                                <div>
                                    <div style={{ fontSize: '12px', color: '#9ca3af', fontWeight: 700, marginBottom: '4px' }}>Username</div>
                                    <div style={{ fontSize: '15px', fontWeight: 800 }}>{user?.username}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '12px', color: '#9ca3af', fontWeight: 700, marginBottom: '4px' }}>Account ID</div>
                                    <div style={{ fontSize: '15px', fontWeight: 800, color: '#6b7280' }}>{user?.id}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '12px', color: '#9ca3af', fontWeight: 700, marginBottom: '4px' }}>Member Since</div>
                                    <div style={{ fontSize: '15px', fontWeight: 800 }}>{new Date().toLocaleDateString()}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '12px', color: '#9ca3af', fontWeight: 700, marginBottom: '4px' }}>Two-Factor Auth</div>
                                    <div style={{ fontSize: '13px', fontWeight: 800, color: user?.twoFactorEnabled ? '#10b981' : '#ef4444' }}>
                                        {user?.twoFactorEnabled ? 'Enabled' : 'Disabled (Contact Support to Enable)'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Other Tabs Placeholder */}
                {['cases', 'transactions'].includes(activeTab) && (
                    <div style={{ background: '#16191f', borderRadius: '12px', padding: '40px', border: '1px solid #1a1c22', textAlign: 'center', color: '#6b7280', fontSize: '16px', fontWeight: 800 }}>
                        {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} coming soon...
                    </div>
                )}

            </div>
        </div>
    );
}
